/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        User Profile & Session Dropdown Menu (Task 10.1.3 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/useAuthStore';
import { useTheme } from '../../context/ThemeProvider';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore';
import { useI18n } from '../../stores/useI18nStore';
import {
  LogOut,
  Settings,
  Key,
  Sun,
  Moon,
  Monitor,
  Shield,
} from 'lucide-react';

export interface UserProfileDropdownProps {
  onNavigate?: (path: string) => void;
}

export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const { user, clearSession } = useAuthStore();
  const { themeMode, setThemeMode } = useTheme();
  const { getActiveWorkspace } = useWorkspaceStore();

  const activeWorkspace = getActiveWorkspace();
  const userName = user?.name || 'Developer Admin';
  const userEmail = user?.email || 'admin@example.com';
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Close dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    clearSession();
    queryClient.clear();
    // Dispatch logout notification
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sophia:user-logout'));
    }
  };

  const handleLinkClick = (path: string) => {
    setIsOpen(false);
    if (onNavigate) {
      onNavigate(path);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-0.5 rounded-full ring-2 ring-transparent hover:ring-primary/40 focus:outline-none focus:ring-primary transition-all"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={userName}
      >
        <div className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 text-accent font-bold flex items-center justify-center text-xs shadow-sm">
          {initials}
        </div>
      </button>

      {/* Popover Menu Panel */}
      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 rounded-2xl bg-surface border border-surface-border shadow-2xl p-2 z-50 space-y-2 animate-fade-in text-xs"
        >
          {/* User Info Header */}
          <div className="px-3 py-2 border-b border-surface-border/60 space-y-1">
            <div className="font-bold text-foreground truncate">{userName}</div>
            <div className="text-[11px] text-foreground-muted truncate font-mono">{userEmail}</div>
            {activeWorkspace && (
              <div className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded-md bg-muted text-foreground-muted font-medium">
                <Shield className="w-3 h-3 text-primary" />
                <span>{activeWorkspace.role ?? 'Member'} in {activeWorkspace.name}</span>
              </div>
            )}
          </div>

          {/* Theme Quick Switcher Strip */}
          <div className="px-2 py-1 space-y-1">
            <span className="text-[10px] font-semibold text-foreground-muted uppercase tracking-wider px-1">
              {t('header.themeMode')}
            </span>
            <div className="grid grid-cols-3 gap-1 bg-muted/50 p-1 rounded-xl border border-surface-border">
              <button
                type="button"
                onClick={() => setThemeMode('light')}
                className={`flex items-center justify-center gap-1 py-1 rounded-lg transition-all ${
                  themeMode === 'light'
                    ? 'bg-surface text-foreground shadow-sm font-semibold'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                <Sun className="w-3 h-3" />
                <span className="text-[10px]">{t('header.light')}</span>
              </button>
              <button
                type="button"
                onClick={() => setThemeMode('dark')}
                className={`flex items-center justify-center gap-1 py-1 rounded-lg transition-all ${
                  themeMode === 'dark'
                    ? 'bg-surface text-foreground shadow-sm font-semibold'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                <Moon className="w-3 h-3" />
                <span className="text-[10px]">{t('header.dark')}</span>
              </button>
              <button
                type="button"
                onClick={() => setThemeMode('system')}
                className={`flex items-center justify-center gap-1 py-1 rounded-lg transition-all ${
                  themeMode === 'system'
                    ? 'bg-surface text-foreground shadow-sm font-semibold'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                <Monitor className="w-3 h-3" />
                <span className="text-[10px]">{t('header.system')}</span>
              </button>
            </div>
          </div>

          {/* Navigation Shortcuts */}
          <div className="space-y-0.5 pt-1 border-t border-surface-border/60">
            <button
              type="button"
              onClick={() => handleLinkClick('/workspace/settings')}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors text-left"
            >
              <Settings className="w-3.5 h-3.5" />
              <span>{t('nav.items.workspaceSettings.label')}</span>
            </button>
            <button
              type="button"
              onClick={() => handleLinkClick('/workspace/developer/keys')}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors text-left"
            >
              <Key className="w-3.5 h-3.5" />
              <span>{t('nav.items.apiKeys.label')}</span>
            </button>
          </div>

          {/* Sign Out Action */}
          <div className="pt-1 border-t border-surface-border/60">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-rose-500 hover:bg-rose-500/10 transition-colors text-left font-medium"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>{t('header.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
